import { ReactNode, RefObject, useEffect, useRef } from "react";
import { SnapshotDetailFooterItem, SnapshotDetailSection } from "@/app/dashboard/SalesSnapshotCard/SalesSnapshotCard";
import styles from "./ProductsSoldDetailModal.module.css"
import IconClose from "@/components/icons/IconClose";
import { useDashboardContext, useDashboardDispatch } from "@/contexts/DashboardContext";
import StatCard from "../StatCard/StatCard";

interface BreakDownCardModalProps {
  isOpen: boolean;
  children: ReactNode;
  buttonRef: RefObject<HTMLButtonElement>;
  onClose: () => void;
  tableRef: RefObject<HTMLDivElement>;
}

interface ProductsSoldDetailModalProps {
  modalPosition: {
    top: number;
    left: number;
  };
}

const ProductsSoldDetailModal = ({
  modalPosition
}: ProductsSoldDetailModalProps) => {
  const modalRef = useRef(null);
  const { productSalesBreakdown } = useDashboardContext()
  const dispatch = useDashboardDispatch()
  const { detailData } = productSalesBreakdown

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        dispatch({ type: 'PRODUCT_SALES_BREAKDOWN_DETAIL_CLOSED' })
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [modalRef, dispatch]);

  const handleCloseButtonClick = () => {
    dispatch({ type: 'PRODUCT_SALES_BREAKDOWN_DETAIL_CLOSED' })
  }

  return (
    <>
      {detailData ? <div className={styles.productsSoldDetailModalContainer} style={{
        top: `${modalPosition.top}px`,
        left: `${modalPosition.left}px`,
      }}
        ref={modalRef}
      >
        <button className={styles.closeButton} onClick={handleCloseButtonClick}>
          <IconClose />
        </button>
        <div className={styles.listingItem}>
          <img src={detailData?.listing.image} alt={detailData?.listing.name} className={styles.listingImage} />
          <div className={styles.listingItemDetail}>
            <p className="strong">{detailData?.listing.name}</p>
            <p className={styles.listingSku}>{detailData?.listing.sku}</p>
          </div>
        </div>
        <StatCard.Content className={styles.statCard}>
          <SnapshotDetailSection detailSection={detailData?.detail?.sales} title="Sales" isFeatured
            formatAs="currency" />
          <SnapshotDetailSection detailSection={detailData?.detail?.units} title="Units" />
          <SnapshotDetailSection detailSection={detailData?.detail?.promotions} title="Promotions"
            formatAs="currency" />
          <SnapshotDetailSection detailSection={detailData?.detail?.shippingCosts} title="Shipping Costs"
            formatAs="currency" />
          <SnapshotDetailSection detailSection={detailData?.detail?.refundCosts} title="Refund Costs"
            formatAs="currency" />
          <SnapshotDetailSection detailSection={detailData?.detail?.amazonFees} title="Amazon Fees"
            formatAs="currency" />
          <SnapshotDetailSection detailSection={detailData?.detail?.costOfGoods} title="Cost of Goods"
            formatAs="currency" />
          <SnapshotDetailSection detailSection={detailData?.detail?.estimatedPayout} title="Estimated Payout"
            formatAs="currency" />
          <SnapshotDetailSection detailSection={detailData?.detail?.grossProfit} title="Gross Profit" isFeatured
            formatAs="currency" />
        </StatCard.Content>
        <StatCard.Content className={styles.footer}>
          <StatCard.List>
            <SnapshotDetailFooterItem detailSection={detailData?.detail?.percentageRefunds} title="% Refunds" formatAs="percent" />
            <SnapshotDetailFooterItem detailSection={detailData?.detail?.margin} title="Margin" formatAs="percent" />
            <SnapshotDetailFooterItem detailSection={detailData?.detail?.roi} title="ROI" formatAs="percent" />
          </StatCard.List>
        </StatCard.Content>
      </div> : null}
    </>
  )
}

export default ProductsSoldDetailModal;